import React, { useState } from 'react';
import { createQuestionnaire, updateQuestionnaire } from '../../services/questionnaireService';

const FIELD_TYPES = ['text', 'textarea', 'radio', 'checkbox', 'select', 'rating', 'date'];
const OPTION_TYPES = new Set(['radio', 'checkbox', 'select']);
const BLANK_FIELD = { label: '', type: 'text', isRequired: false, options: [], _optionInput: '' };

/**
 * QuestionnaireEditor — create or edit a questionnaire.
 *
 * Props:
 *   initial   {object|null}  Existing questionnaire to edit, or null for create.
 *   tenantId  {string}       Current community tenant ID.
 *   projects  {object[]}     Projects available for this tenant (for project selector).
 *   onSave    {function}     Called with the saved questionnaire document.
 *   onCancel  {function}     Cancel without saving.
 */
const QuestionnaireEditor = ({ initial, tenantId, projects, onSave, onCancel }) => {
  const isNew = !initial?._id;

  const [form, setForm] = useState({
    title:       initial?.title       || '',
    description: initial?.description || '',
    projectId:   initial?.projectId   || projects[0]?._id || '',
    isActive:    initial?.isActive    !== false,
    fields: (initial?.fields || []).map((f) => ({
      ...f,
      _optionInput: (f.options || []).join(', '),
    })),
  });

  const [addingField, setAddingField] = useState(false);
  const [newField, setNewField]       = useState({ ...BLANK_FIELD });
  const [msg, setMsg]                 = useState('');
  const [saving, setSaving]           = useState(false);

  // ── Field builder helpers ─────────────────────────────────────────────────

  const commitNewField = () => {
    if (!newField.label.trim()) return;
    const fieldKey = newField.label.toLowerCase().replace(/[^a-z0-9]+/g, '_');
    const options  = OPTION_TYPES.has(newField.type)
      ? newField._optionInput.split(',').map((s) => s.trim()).filter(Boolean)
      : [];
    setForm((prev) => ({
      ...prev,
      fields: [...prev.fields, { ...newField, fieldKey, options }],
    }));
    setNewField({ ...BLANK_FIELD });
    setAddingField(false);
  };

  const removeField = (idx) =>
    setForm((prev) => ({ ...prev, fields: prev.fields.filter((_, i) => i !== idx) }));

  // ── Save ──────────────────────────────────────────────────────────────────

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.projectId) { setMsg('Please select a project.'); return; }
    setSaving(true);
    setMsg('');
    try {
      // Strip internal _optionInput, attach stable order index
      const fields = form.fields.map(({ _optionInput, ...f }, i) => ({ ...f, order: i }));
      const res = isNew
        ? await createQuestionnaire({ ...form, fields, tenantId })
        : await updateQuestionnaire(initial._id, { ...form, fields });
      onSave(res.data);
    } catch (err) {
      setMsg(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <form className="questionnaire-editor" onSubmit={handleSubmit}>
      <h3>{isNew ? 'New Questionnaire' : `Edit: ${initial.title}`}</h3>

      <label>
        Project
        <select
          value={form.projectId}
          onChange={(e) => setForm({ ...form, projectId: e.target.value })}
          required
          disabled={!isNew}
        >
          <option value="">-- Select project --</option>
          {projects.map((p) => (
            <option key={p._id} value={p._id}>{p.title}</option>
          ))}
        </select>
      </label>

      <label>
        Title
        <input
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          required
        />
      </label>

      <label>
        Description
        <textarea
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          rows={2}
        />
      </label>

      <label className="checkbox-label">
        <input
          type="checkbox"
          checked={form.isActive}
          onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
        />
        Active (visible to members)
      </label>

      {/* ── Field builder ──────────────────────────────────────────────── */}
      <fieldset>
        <legend>Questions ({form.fields.length})</legend>

        {form.fields.length > 0 && (
          <table className="admin-table fields-table">
            <thead>
              <tr>
                <th>#</th><th>Label</th><th>Type</th><th>Required</th><th>Options</th><th></th>
              </tr>
            </thead>
            <tbody>
              {form.fields.map((f, i) => (
                <tr key={i}>
                  <td>{i + 1}</td>
                  <td>{f.label}</td>
                  <td>{f.type}</td>
                  <td>{f.isRequired ? '✓' : '—'}</td>
                  <td>{f.options?.length ? f.options.join(', ') : '—'}</td>
                  <td>
                    <button
                      type="button"
                      className="btn-danger btn-sm"
                      onClick={() => removeField(i)}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {addingField ? (
          <div className="add-field-form">
            <label>
              Label
              <input
                value={newField.label}
                onChange={(e) => setNewField({ ...newField, label: e.target.value })}
                autoFocus
                placeholder="e.g. How satisfied are you?"
              />
            </label>

            <label>
              Type
              <select
                value={newField.type}
                onChange={(e) => setNewField({ ...newField, type: e.target.value })}
              >
                {FIELD_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </label>

            {OPTION_TYPES.has(newField.type) && (
              <label>
                Options <small>(comma-separated)</small>
                <input
                  value={newField._optionInput}
                  onChange={(e) => setNewField({ ...newField, _optionInput: e.target.value })}
                  placeholder="Option A, Option B, Option C"
                />
              </label>
            )}

            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={newField.isRequired}
                onChange={(e) => setNewField({ ...newField, isRequired: e.target.checked })}
              />
              Required
            </label>

            <div className="form-actions">
              <button type="button" onClick={commitNewField} disabled={!newField.label.trim()}>
                Add Question
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => { setAddingField(false); setNewField({ ...BLANK_FIELD }); }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button type="button" className="btn-secondary" onClick={() => setAddingField(true)}>
            + Add Question
          </button>
        )}
      </fieldset>

      {msg && <p className="error-msg">{msg}</p>}

      <div className="form-actions">
        <button type="submit" disabled={saving}>
          {saving ? 'Saving…' : isNew ? 'Create Questionnaire' : 'Save Changes'}
        </button>
        <button type="button" className="btn-secondary" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
};

export default QuestionnaireEditor;
